import { useState, useEffect } from 'react';
import { Plus, Trash2, X, Tag, Clock, Package, RefreshCw, AlertCircle, CheckCircle, Search } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { promotionsAPI, adminAPI } from '../../utils/api';
import toast from 'react-hot-toast';

const EMPTY = { productId: '', discount: '', expiresAt: '' };

export default function AdminPromotions() {
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const load = () => {
    setLoading(true);
    Promise.all([
      promotionsAPI.getAll(),
      adminAPI.getProducts(),
    ]).then(([promoRes, prodRes]) => {
      setPromotions(promoRes.data.promotions);
      setProducts(prodRes.data.products);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Check expired promos on mount
  useEffect(() => {
    promotionsAPI.checkExpired().catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.productId) return toast.error('Sélectionne un produit');
    if (!form.discount || form.discount < 1 || form.discount > 99) return toast.error('Réduction entre 1 et 99%');
    if (!form.expiresAt) return toast.error('Date d\'expiration requise');
    if (new Date(form.expiresAt) <= new Date()) return toast.error('La date doit être dans le futur');

    setSaving(true);
    try {
      await promotionsAPI.create(form);
      toast.success('Promotion créée ! Le produit est maintenant en promo 🎉');
      setModal(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
    setSaving(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer la promotion pour "${name}" ? Le prix original sera restauré.`)) return;
    try {
      await promotionsAPI.delete(id);
      toast.success('Promotion supprimée — prix original restauré');
      load();
    } catch { toast.error('Erreur'); }
  };

  const selectedProduct = products.find(p => p._id === form.productId);
  const previewPrice = selectedProduct && form.discount
    ? (selectedProduct.price * (1 - Number(form.discount) / 100)).toFixed(2)
    : null;

  const filteredProducts = products.filter(p =>
    !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.brand.toLowerCase().includes(productSearch.toLowerCase())
  );

  const now = new Date();
  const activePromos = promotions.filter(p => p.active && new Date(p.expiresAt) > now);
  const expiredPromos = promotions.filter(p => !p.active || new Date(p.expiresAt) <= now);

  // Min date = tomorrow
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  return (
    <AdminLayout title="Promotions">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700 }}>Promotions produits</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
            Appliquer une réduction sur un produit — le prix sera barré dans la boutique
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={load} title="Rafraîchir"><RefreshCw size={14} /></button>
          <button className="btn btn-primary" onClick={() => { setForm(EMPTY); setProductSearch(''); setModal(true); }}>
            <Plus size={17} /> Nouvelle promotion
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="admin-promo-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Promotions actives', value: activePromos.length, color: 'var(--success)', icon: CheckCircle },
          { label: 'Expirées', value: expiredPromos.length, color: 'var(--danger)', icon: Clock },
          { label: 'Total produits en promo', value: activePromos.length, color: 'var(--accent)', icon: Tag },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={22} style={{ color }} />
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, color }}>{value}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex-center" style={{ height: 200 }}><div className="spinner" /></div>
      ) : (
        <>
          {/* Active promotions */}
          <h3 style={{ fontWeight: 700, marginBottom: 12, fontSize: 16 }}>
            🟢 Promotions actives ({activePromos.length})
          </h3>
          {activePromos.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 40, marginBottom: 24 }}>
              <Tag size={40} style={{ color: 'var(--text-muted)', opacity: 0.4, marginBottom: 12 }} />
              <div style={{ color: 'var(--text-muted)' }}>Aucune promotion active</div>
              <button className="btn btn-primary btn-sm" style={{ marginTop: 16 }} onClick={() => setModal(true)}>
                Créer une promotion
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
              {activePromos.map(promo => {
                const daysLeft = Math.ceil((new Date(promo.expiresAt) - now) / (1000 * 60 * 60 * 24));
                return (
                  <div key={promo._id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px' }}>
                    {/* Thumbnail */}
                    {promo.product?.thumbnail ? (
                      <img src={promo.product.thumbnail} alt="" style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 56, height: 56, borderRadius: 12, background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Package size={22} style={{ color: 'var(--text-muted)' }} />
                      </div>
                    )}
                    {/* Product info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>
                        {promo.productName || promo.product?.name}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{promo.product?.brand}</div>
                    </div>
                    {/* Discount badge */}
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <div style={{ background: 'rgba(244,63,94,0.12)', color: 'var(--danger)', padding: '4px 14px', borderRadius: 20, fontWeight: 800, fontSize: 18 }}>
                        -{promo.discount}%
                      </div>
                    </div>
                    {/* Price */}
                    <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 100 }}>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'line-through' }}>${promo.originalPrice?.toLocaleString()}</div>
                      <div style={{ fontWeight: 800, color: 'var(--gold)', fontSize: 18 }}>${promo.promoPrice?.toLocaleString()}</div>
                    </div>
                    {/* Expiry */}
                    <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 90 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: daysLeft <= 3 ? 'var(--danger)' : daysLeft <= 7 ? 'var(--warning)' : 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}>
                        <Clock size={12} />
                        {daysLeft === 1 ? 'Expire demain' : `${daysLeft}j restants`}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {new Date(promo.expiresAt).toLocaleDateString()}
                      </div>
                    </div>
                    {/* Delete */}
                    <button
                      className="btn btn-sm btn-icon"
                      style={{ background: 'rgba(244,63,94,0.1)', color: 'var(--danger)', border: 'none', flexShrink: 0 }}
                      onClick={() => handleDelete(promo._id, promo.productName || promo.product?.name)}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Expired */}
          {expiredPromos.length > 0 && (
            <>
              <h3 style={{ fontWeight: 700, marginBottom: 12, fontSize: 16, color: 'var(--text-muted)' }}>
                🔴 Expirées ({expiredPromos.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {expiredPromos.map(promo => (
                  <div key={promo._id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 20px', opacity: 0.6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{promo.productName || promo.product?.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Expirée le {new Date(promo.expiresAt).toLocaleDateString()}</div>
                    </div>
                    <div style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)', padding: '3px 12px', borderRadius: 20, fontWeight: 700, fontSize: 14 }}>
                      -{promo.discount}%
                    </div>
                    <button
                      className="btn btn-sm btn-icon"
                      style={{ background: 'rgba(244,63,94,0.1)', color: 'var(--danger)', border: 'none' }}
                      onClick={() => handleDelete(promo._id, promo.productName)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* ── MODAL CREATE ── */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal-box" style={{ maxWidth: 580 }}>
            <div className="modal-header">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>
                Nouvelle promotion
              </h2>
              <button onClick={() => setModal(false)} className="btn btn-ghost btn-icon"><X size={20} /></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreate}>

                {/* Product selector */}
                <div className="form-group">
                  <label className="form-label">Produit *</label>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      className="form-input"
                      placeholder="Rechercher un produit..."
                      value={productSearch}
                      onChange={e => setProductSearch(e.target.value)}
                      style={{ paddingLeft: 40 }}
                    />
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: '1.5px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                    {filteredProducts.length === 0 && (
                      <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Aucun produit trouvé</div>
                    )}
                    {filteredProducts.map(p => (
                      <div
                        key={p._id}
                        onClick={() => { f('productId', p._id); setProductSearch(p.name); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                          cursor: 'pointer',
                          background: form.productId === p._id ? 'var(--accent-dim)' : 'var(--bg-secondary)',
                          borderBottom: '1px solid var(--border)',
                          transition: 'var(--transition)',
                        }}
                        onMouseEnter={e => { if (form.productId !== p._id) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                        onMouseLeave={e => { if (form.productId !== p._id) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                      >
                        {p.thumbnail
                          ? <img src={p.thumbnail} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                          : <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--bg-card)', flexShrink: 0 }} />
                        }
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: form.productId === p._id ? 'var(--accent)' : 'var(--text-primary)' }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.brand} · {p.category}</div>
                        </div>
                        <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: 14, flexShrink: 0 }}>${p.price.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Discount % */}
                <div className="form-group">
                  <label className="form-label">Réduction (%) *</label>
                  <input
                    className="form-input"
                    type="number" min="1" max="99"
                    value={form.discount}
                    onChange={e => f('discount', e.target.value)}
                    placeholder="Ex: 20"
                    required
                  />
                </div>

                {/* Expiry date */}
                <div className="form-group">
                  <label className="form-label">Date d'expiration *</label>
                  <input
                    className="form-input"
                    type="date"
                    value={form.expiresAt}
                    onChange={e => f('expiresAt', e.target.value)}
                    min={minDateStr}
                    required
                  />
                </div>

                {/* Live preview */}
                {selectedProduct && form.discount > 0 && (
                  <div style={{ padding: '16px 18px', background: 'rgba(34,197,94,0.08)', border: '1.5px solid rgba(34,197,94,0.2)', borderRadius: 14, marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--success)', marginBottom: 10 }}>✓ Aperçu dans la boutique</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {selectedProduct.thumbnail && <img src={selectedProduct.thumbnail} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover' }} />}
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{selectedProduct.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'line-through' }}>${selectedProduct.price.toLocaleString()}</span>
                          <span style={{ fontWeight: 800, color: 'var(--gold)', fontSize: 18 }}>${previewPrice}</span>
                          <span style={{ background: 'rgba(244,63,94,0.12)', color: 'var(--danger)', padding: '2px 10px', borderRadius: 20, fontWeight: 700, fontSize: 12 }}>-{form.discount}%</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--success)', marginTop: 4 }}>
                          Économie de ${(selectedProduct.price - previewPrice).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Warn if product already has promo */}
                {selectedProduct?.discount > 0 && (
                  <div style={{ display: 'flex', gap: 10, padding: '12px 16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, marginBottom: 16 }}>
                    <AlertCircle size={16} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      Ce produit a déjà une remise de <strong>{selectedProduct.discount}%</strong>. La nouvelle promotion remplacera l'ancienne.
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Annuler</button>
                  <button type="submit" className="btn btn-primary" disabled={saving || !form.productId}>
                    <Tag size={16} />
                    {saving ? 'Création...' : 'Créer la promotion'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
